<template>
    <section class="relative overflow-hidden border-b border-green-600 bg-gray-200 dark:bg-gray-800">
        <PagesPageHeader
            :breadcrumbs="breadcrumbs"
            title="Plantation Challenges"
            description="Embarking on the journey of agarwood cultivation requires an in-depth understanding."
            icon="i-ph-potted-plant"
            iconColor="green"
            color="blue"
            :image="true"
            src="/assets/images/sliders/slider-03.webp" />
    </section>

    <ULandingSection
        title="Wood of the Gods"
        description="Agarwood, revered as the “Wood of the Gods,” is one of the world’s most treasured natural resources."
        :features="[
            {
                name: 'Understanding Agarwood Cultivation',
                description:
                    'Embarking on the journey of agarwood cultivation requires an in-depth understanding of the biological and environmental factors crucial for fostering high-quality agarwood. The Aquilaria tree produces agarwood as a defense against a specific type of mold infection.',
                icon: 'i-ph-potted-plant'
            },
            {
                name: 'Starting your Plantation',
                description:
                    'Initiating your agarwood plantation begins with selecting the right location and climate, typically in parts of South Asia where the conditions are ideal. It is vital to understand and test the soil’s quality to ensure it’s fertile and well-drained.',
                icon: 'i-ph-potted-plant'
            }
        ]"
        align="left"
        :ui="{
            title: 'text-3xl font-bold text-green-600 sm:text-4xl lg:text-4xl'
        }">
        <NuxtImg
            format="webp"
            width="840"
            height="473"
            class="w-[100%] rounded-xl"
            src="/assets/images/gallery/gallery-01.webp"
            :alt="config.public.company.name" />
    </ULandingSection>

    <UDivider
        icon="i-ph-plant"
        :ui="{
            icon: {
                base: 'flex-shrink-0 w-10 h-10 text-green-600'
            }
        }" />

    <ULandingSection
        title="Risks and Pitfalls"
        description="Several risks and pitfalls accompany agarwood cultivation. The infection process, which induces the resin production, is complex and somewhat unpredictable."
        :features="[
            {
                name: 'Challenges in Agarwood Cultivation',
                description:
                    'The cultivation of agarwood comes with its set of challenges. Sustainability concerns such as overharvesting and illegal trade are significant. It’s imperative to adhere to sustainable practices and regulations to ensure the plantation’s long-term viability and the industry’s ethical standing.',
                icon: 'i-ph-potted-plant'
            },
            {
                name: 'Mitigating Risks and Overcoming Challenges',
                description:
                    'Mitigating these risks and overcoming challenges requires a multifaceted approach. Continuous education and training in agarwood cultivation techniques, market trends, and sustainability practices are vital. Engaging with the agarwood community, including cultivators, researchers, and industry bodies, can provide invaluable insights and support.',
                icon: 'i-ph-potted-plant'
            }
        ]"
        align="right"
        :ui="{
            title: 'text-3xl font-bold text-green-600 sm:text-4xl lg:text-4xl'
        }">
        <NuxtImg
            format="webp"
            width="840"
            height="473"
            class="w-[100%] rounded-xl"
            src="/assets/images/gallery/gallery-02.webp"
            :alt="config.public.company.name" />
    </ULandingSection>

    <PagesCalloutsPlantationOwnership />
</template>

<script setup>
    definePageMeta({
        layout: 'default'
    })

    const config = useRuntimeConfig()
    const router = useRouter()

    useHead({
        title: `${config.public.company.name} | Plantation Challenges`,
        meta: [
            {
                name: 'description',
                content: 'Embarking on the journey of agarwood cultivation requires an in-depth understanding.'
            },
            { name: 'robots', content: 'index, follow' }
        ],
        link: [{ rel: 'canonical', href: `${config.public.base.url + router.currentRoute.value.path}` }]
    })

    const breadcrumbs = [{ label: 'Home', to: '/' }, { label: 'Plantations', to: '/plantations' }, { label: 'Plantation Challenges' }]
</script>
